import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import stockCategoryService from "../../services/stockCategoryService";
import lotService from "../../services/lotService";
import reportService from "../../services/reportService";
import { formatDate } from "../../utils/dateUtils";
import {
  getReportTheme,
  loadLogoImage,
  drawPdfHeader,
  formatNumber,
  formatWeight,
  formatCurrency,
  formatRate,
  getDetailAddition,
  getDetailDeduction,
  downloadBlob,
  escapeHtml,
  generateExcelHtmlFromData,
  generatePDFFromData,
  generateSummaryExcelHtmlFromData,
  generateSummaryPDFFromData
} from "../../utils/stockReportExport";

function StockReportPage() {
  const navigate = useNavigate();
  const { stockCategoryId } = useEncryptedParams();
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    stockCategoryId || "",
  );
  const [selectedLotId, setSelectedLotId] = useState("");
  const [activeCategories, setActiveCategories] = useState([]);
  const [activeLots, setActiveLots] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState("");
  const [error, setError] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const csvDropdownRef = useRef(null);
  const pdfDropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        csvDropdownRef.current &&
        !csvDropdownRef.current.contains(event.target) &&
        pdfDropdownRef.current &&
        !pdfDropdownRef.current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedCategoryId(stockCategoryId || "");
  }, [stockCategoryId]);

  const loadOptions = useCallback(async () => {
    setIsOptionsLoading(true);
    try {
      const [categoriesData, lotsData] = await Promise.all([
        stockCategoryService.getActiveStockCategories(),
        lotService.getActiveLots({ showLocked: true }),
      ]);
      setActiveCategories(categoriesData.items || []);
      setActiveLots(lotsData.items || []);
    } catch (err) {
      setError(err?.message || "Failed to load filter options.");
    } finally {
      setIsOptionsLoading(false);
    }
  }, []);

  const loadReport = useCallback(async (currentCategoryId, currentLotId) => {
    setIsLoading(true);
    setError("");
    try {
      const report = await reportService.getStockReport(
        currentCategoryId,
        currentLotId,
      );
      setRows(report.items || []);
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load stock report.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    loadReport(selectedCategoryId, selectedLotId);
  }, [selectedCategoryId, selectedLotId, loadReport]);

  const categoryOptions = useMemo(() => {
    const options = activeCategories.map((cat) => ({
      value: String(cat.id || cat.stockCategoryId),
      label: cat.name || cat.stockCategoryName,
    }));

    return [{ value: "", label: "All Categories" }, ...options];
  }, [activeCategories]);

  const lotOptions = useMemo(() => {
    const options = activeLots.map((lot) => ({
      value: String(lot.lotId),
      label: lot.lotNo || "-",
    }));

    return [{ value: "", label: "All Lots" }, ...options];
  }, [activeLots]);

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setSelectedCategoryId(value);
    if (!selectedLotId) {
      navigate(
        value ? `/reports/stock-report/${encrypt(value)}` : "/reports/stock-report",
      );
    }
  };

  const handleLotChange = (event) => {
    setSelectedLotId(event.target.value);
  };

  const handleRefresh = () => {
    loadOptions();
    loadReport(selectedCategoryId, selectedLotId);
  };

  const handleDownload = async (format, reportType = "detailed") => {
    const actionKey = `${format}-${reportType}`;
    setDownloadingFormat(actionKey);
    setError("");
    setOpenDropdown(null);
    try {
      const scope = [];
      if (selectedCategoryId) scope.push(`cat-${selectedCategoryId}`);
      if (selectedLotId) scope.push(`lot-${selectedLotId}`);
      const scopeStr = scope.length > 0 ? scope.join("-") : "all";
      const filePrefix = reportType === "summary" ? "stock-summary-report" : "stock-report";

      const theme = getReportTheme();
      const logoImg = await loadLogoImage(theme.logoSrc);

      if (reportType === "summary") {
        if (format === "csv") {
          const html = generateSummaryExcelHtmlFromData(rows);
          const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
          downloadBlob(blob, `${filePrefix}-${scopeStr}.xls`);
        } else if (format === "pdf") {
          const doc = generateSummaryPDFFromData(rows, logoImg, theme);
          doc.save(`${filePrefix}-${scopeStr}.pdf`);
        } else {
          const blob = await reportService.downloadStockSummaryReport(
            format,
            selectedCategoryId,
            selectedLotId,
          );
          downloadBlob(blob, `${filePrefix}-${scopeStr}.${format}`);
        }
      } else {
        if (format === "csv") {
          const html = generateExcelHtmlFromData(rows);
          const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
          downloadBlob(blob, `${filePrefix}-${scopeStr}.xls`);
        } else if (format === "pdf") {
          const doc = generatePDFFromData(rows, logoImg, theme);
          doc.save(`${filePrefix}-${scopeStr}.pdf`);
        } else {
          const blob = await reportService.downloadStockReport(
            format,
            selectedCategoryId,
            selectedLotId,
          );
          downloadBlob(blob, `${filePrefix}-${scopeStr}.${format}`);
        }
      }
    } catch (err) {
      setError(
        err?.message || `Failed to download ${format.toUpperCase()} report.`,
      );
    } finally {
      setDownloadingFormat("");
    }
  };

  const toggleRow = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 dark:border-slate-800 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Reports
          </p>
          <h1 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
            Stock Report
          </h1>
        </div>

        <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto lg:items-end">
          <div className="w-full sm:min-w-[200px]">
            <SearchableDropdown
              label="Stock Category"
              name="stockCategoryId"
              value={selectedCategoryId}
              onChange={handleCategoryChange}
              options={categoryOptions}
              placeholder={isOptionsLoading ? "Loading..." : "All Categories"}
              disabled={isOptionsLoading}
            />
          </div>
          <div className="w-full sm:min-w-[200px]">
            <SearchableDropdown
              label="Lot"
              name="lotId"
              value={selectedLotId}
              onChange={handleLotChange}
              options={lotOptions}
              placeholder={isOptionsLoading ? "Loading..." : "All Lots"}
              disabled={isOptionsLoading}
            />
          </div>
          <Button
            variant="secondary"
            onClick={handleRefresh}
            isLoading={isLoading || isOptionsLoading}
          >
            Refresh
          </Button>
          <div className="relative inline-block text-left" ref={csvDropdownRef}>
            <Button
              variant="secondary"
              onClick={() => setOpenDropdown((prev) => (prev === "csv" ? null : "csv"))}
              isLoading={downloadingFormat.startsWith("csv")}
              disabled={Boolean(downloadingFormat)}
              className="inline-flex items-center gap-1.5"
            >
              <span>CSV</span>
              <svg
                className={`h-3.5 w-3.5 transition-transform duration-200 ${
                  openDropdown === "csv" ? "rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Button>

            {openDropdown === "csv" && (
              <div className="absolute right-0 z-30 mt-1.5 w-60 origin-top-right rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl dark:border-slate-800 dark:bg-slate-900">
                <button
                  type="button"
                  onClick={() => handleDownload("csv", "summary")}
                  className="flex w-full items-start gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800 transition-colors"
                >
                  <span className="mt-0.5 text-base">📊</span>
                  <div>
                    <div className="font-bold text-slate-900 dark:text-white">Summary Report</div>
                    <div className="text-[11px] font-normal text-slate-500 dark:text-slate-400">
                      Category totals & Grand Total
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload("csv", "detailed")}
                  className="flex w-full items-start gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800 transition-colors"
                >
                  <span className="mt-0.5 text-base">📑</span>
                  <div>
                    <div className="font-bold text-slate-900 dark:text-white">Detailed Report</div>
                    <div className="text-[11px] font-normal text-slate-500 dark:text-slate-400">
                      All date-wise transactions
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>

          <div className="relative inline-block text-left" ref={pdfDropdownRef}>
            <Button
              variant="secondary"
              onClick={() => setOpenDropdown((prev) => (prev === "pdf" ? null : "pdf"))}
              isLoading={downloadingFormat.startsWith("pdf")}
              disabled={Boolean(downloadingFormat)}
              className="inline-flex items-center gap-1.5"
            >
              <span>PDF</span>
              <svg
                className={`h-3.5 w-3.5 transition-transform duration-200 ${
                  openDropdown === "pdf" ? "rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Button>

            {openDropdown === "pdf" && (
              <div className="absolute right-0 z-30 mt-1.5 w-60 origin-top-right rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl dark:border-slate-800 dark:bg-slate-900">
                <button
                  type="button"
                  onClick={() => handleDownload("pdf", "summary")}
                  className="flex w-full items-start gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800 transition-colors"
                >
                  <span className="mt-0.5 text-base">📊</span>
                  <div>
                    <div className="font-bold text-slate-900 dark:text-white">Summary Report</div>
                    <div className="text-[11px] font-normal text-slate-500 dark:text-slate-400">
                      Category totals & Grand Total
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload("pdf", "detailed")}
                  className="flex w-full items-start gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800 transition-colors"
                >
                  <span className="mt-0.5 text-base">📑</span>
                  <div>
                    <div className="font-bold text-slate-900 dark:text-white">Detailed Report</div>
                    <div className="text-[11px] font-normal text-slate-500 dark:text-slate-400">
                      All date-wise transactions
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm font-medium text-danger">
          {error}
        </div>
      )}

      {/* <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Total Addition
          </p>
          <p className="mt-1 text-2xl font-black text-emerald-600">
            {formatWeight(
              rows.reduce((acc, curr) => acc + Number(curr.totalAddition || 0), 0)
            )}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Total Deduction
          </p>
          <p className="mt-1 text-2xl font-black text-rose-600">
            {formatWeight(
              rows.reduce((acc, curr) => acc + Number(curr.totalDeduction || 0), 0)
            )}
          </p>
        </div>
        <div className="rounded-xl border border-primary-200 bg-primary-50 p-4 shadow-sm dark:border-primary-900/50 dark:bg-primary-900/10">
          <p className="text-[10px] font-bold uppercase tracking-wider text-primary-700 dark:text-primary-300">
            Remaining Stock
          </p>
          <p className="mt-1 text-2xl font-black text-primary-800 dark:text-primary-200">
            {formatWeight(
              rows.reduce((acc, curr) => acc + Number(curr.remainingStock || 0), 0)
            )}
          </p>
        </div>
      </div> */}

      <div className="flex min-h-0 flex-col overflow-visible rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="relative overflow-auto" >
          <table className="w-full min-w-max border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-slate-50/80 backdrop-blur-md dark:bg-slate-800/80">
              <tr>
                <th className="border-b border-slate-200 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400 w-16">
                  Sr. No.
                </th>
                <th className="border-b border-slate-200 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400 w-16">
                  Details
                </th>
                <th className="border-b border-slate-200 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Stock Category
                </th>
                <th className="border-b border-slate-200 px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Addition (KG)
                </th>
                <th className="border-b border-slate-200 px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Deduction (KG)
                </th>
                
                <th className="border-b border-slate-200 px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Remaining Stock (KG)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
                      <p className="text-xs font-medium text-slate-500">
                        Loading records...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <svg className="h-10 w-10 text-slate-300 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                        No records found
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((item, index) => {
                  const isExpanded = expandedRows[index];
                  const stockData = item.stockData || [];

                  return (
                    <React.Fragment key={index}>
                      <tr
                        className={`group transition-colors cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/50 ${isExpanded ? "bg-slate-50/80 dark:bg-slate-800/80" : ""}`}
                        onClick={() => toggleRow(index)}
                      >
                        <td className="px-4 py-3 text-xs font-medium text-slate-500">
                          {index + 1}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            className={`flex h-6 w-6 items-center justify-center rounded-full transition-all ${
                              isExpanded
                                ? "bg-primary-100 text-primary-600 dark:bg-primary-900/50 dark:text-primary-400"
                                : "bg-slate-100 text-slate-500 group-hover:bg-primary-50 group-hover:text-primary-500 dark:bg-slate-800 dark:text-slate-400"
                            }`}
                          >
                            <svg
                              className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-slate-900 dark:text-white">
                          {item.stockCategoryName}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-mono font-bold text-emerald-600">
                          {formatWeight(item.totalAddition)}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-mono font-bold text-rose-600">
                          {formatWeight(item.totalDeduction)}
                        </td>
                        
                        <td className="px-4 py-3 text-right text-xs font-mono font-black text-primary-700 dark:text-primary-400">
                          {formatWeight(item.remainingStock)}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td
                            colSpan="7"
                            className="bg-slate-50/50 px-6 py-4 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800"
                          >
                            <div className="max-w-5xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                              <div className="bg-slate-100 px-4 py-2 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Transaction History</span>
                              </div>
                              <table className="w-full text-left">
                                <thead className="bg-slate-50/50 dark:bg-slate-800/50">
                                  <tr>
                                    <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Date</th>
                                    <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Lot</th>
                                    <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Addition (KG)</th>
                                    <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Deduction (KG)</th>
                                    <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Cumulative (KG)</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                  {stockData.length === 0 ? (
                                    <tr>
                                      <td colSpan="8" className="px-4 py-8 text-center text-xs font-medium text-slate-500">
                                        No transaction records found.
                                      </td>
                                    </tr>
                                  ) : (
                                    stockData.map((detail, dIndex) => (
                                      <tr key={dIndex} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                                        <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                                          {formatDate(detail.transactionDate)}
                                        </td>
                                        
                                        <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                                          {detail.lotNo || detail.sourceLotNo || "-"}
                                        </td>
                                        <td className="px-4 py-2 text-right text-xs font-mono font-medium text-emerald-600">
                                          {getDetailAddition(detail) > 0 ? `+${formatWeight(getDetailAddition(detail))}` : "-"}
                                        </td>
                                        <td className="px-4 py-2 text-right text-xs font-mono font-medium text-rose-600">
                                          {getDetailDeduction(detail) > 0 ? `-${formatWeight(getDetailDeduction(detail))}` : "-"}
                                        </td>
                                        
                                        <td className="px-4 py-2 text-right text-xs font-mono font-bold text-slate-900 dark:text-white">
                                          {formatWeight(detail.cumulativeStock)}
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default StockReportPage;
